/** Specials use the restaurant's time zone, regardless of the manager's device. */
export const SPECIALS_TIME_ZONE = 'America/Chicago';
const chicagoClock = new Intl.DateTimeFormat('en-US', {
  timeZone: SPECIALS_TIME_ZONE,
  year: 'numeric', month: '2-digit', day: '2-digit',
  hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23'
});

function clockParts(value: Date) {
  const parts = Object.fromEntries(chicagoClock.formatToParts(value).map((part) => [part.type, part.value]));
  return { date: `${parts.year}-${parts.month}-${parts.day}`, time: `${parts.hour}:${parts.minute}`, second: parts.second };
}

export function splitSpecialDateTime(value?: string | null) {
  if (!value) return { date: '', time: '' };
  const instant = new Date(value);
  if (!Number.isFinite(instant.getTime())) throw new Error('The saved special time is invalid.');
  const { date, time } = clockParts(instant);
  return { date, time };
}

/** An unchanged original retains its exact instant, including a fall-back occurrence. */
export function combineSpecialDateTime(date: string, time: string, original?: string | null): string | null {
  if (!date) {
    if (time) throw new Error('Choose a date for the Central Time entered, or clear both fields.');
    return null;
  }
  const clock = time || '00:00';
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || !/^([01]\d|2[0-3]):[0-5]\d$/.test(clock)) {
    throw new Error('Enter a valid date and Central Time.');
  }
  const wall = Date.parse(`${date}T${clock}:00Z`);
  if (!Number.isFinite(wall) || new Date(wall).toISOString().slice(0, 16) !== `${date}T${clock}`) {
    throw new Error('Enter a valid calendar date and Central Time.');
  }
  if (original) {
    const saved = splitSpecialDateTime(original);
    if (saved.date === date && saved.time === clock) return original;
  }
  // Discover actual IANA offsets on both sides of a possible DST transition.
  const offsets = new Set<number>();
  for (const hours of [-36, -12, 0, 12, 36]) {
    const instant = wall + hours * 3_600_000;
    const parts = clockParts(new Date(instant));
    offsets.add(Date.parse(`${parts.date}T${parts.time}:${parts.second}Z`) - instant);
  }
  const candidates = [...offsets].map((offset) => wall - offset).filter((instant) => {
    const parts = clockParts(new Date(instant));
    return parts.date === date && parts.time === clock;
  });
  if (!candidates.length) throw new Error('That Central Time does not exist when clocks spring forward. Choose a time before 2:00 AM or at/after 3:00 AM.');
  if (candidates.length > 1) throw new Error('That Central Time occurs twice when clocks fall back. Choose a time before 1:00 AM or at/after 2:00 AM. An unchanged saved time keeps its original occurrence.');
  return new Date(candidates[0]!).toISOString();
}

export function validateSpecialDateRange(startsAt: string | null, endsAt: string | null) {
  if (startsAt && endsAt && Date.parse(endsAt) <= Date.parse(startsAt)) {
    throw new Error('End date and time must be after the start date and time.');
  }
}
